import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../App';

const OUTCOME = {
  queued: ['bg-slate-100 text-slate-700', 'Queued'], in_progress: ['bg-sky-100 text-sky-800', 'On the call'],
  booked: ['bg-emerald-100 text-emerald-800', 'Booked'], callback: ['bg-amber-100 text-amber-800', 'Call back'],
  not_interested: ['bg-rose-100 text-rose-700', 'Not interested'], no_answer: ['bg-slate-100 text-slate-500', 'No answer'],
  failed: ['bg-rose-100 text-rose-700', 'Failed'], call_task: ['bg-amber-50 text-amber-800', 'Rep call task'],
};
const badge = (s) => { const [c, l] = OUTCOME[s] || OUTCOME.queued; return <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${c}`}>{l}</span>; };

export default function VoiceCloser() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [calls, setCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadId, setLeadId] = useState(params.get('lead') || '');
  const [goal, setGoal] = useState('qualify_book');
  const [placing, setPlacing] = useState(false);
  const [open, setOpen] = useState(null);
  const [bookAt, setBookAt] = useState('');
  const [booking, setBooking] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, c, l] = await Promise.all([
        api.get('/api/voice/status'),
        api.get('/api/voice/calls'),
        api.get('/api/leads', { params: { limit: 100 } }),
      ]);
      setStatus(s.data); setCalls(c.data.calls || []);
      const list = l.data.leads || l.data || []; setLeads(list);
      setLeadId(prev => prev || (list[0]?.id || ''));
    } catch { toast.error('Could not load Voice AI'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const place = async () => {
    if (!leadId) return toast.error('Pick a lead first');
    const lead = leads.find(l => l.id === leadId);
    if (!lead?.phone) return toast.error('Lead has no phone — run skip-trace first');
    setPlacing(true);
    try {
      const r = await api.post('/api/voice/calls', { lead_id: leadId, goal });
      toast.success(r.data.mode === 'live-voice' ? 'Voice AI is dialing now' : 'Voice not connected — AI talk-track queued for the rep');
      setOpen(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Call failed'); }
    setPlacing(false);
  };

  const view = async (id) => {
    try { const r = await api.get(`/api/voice/calls/${id}`); setOpen(r.data); }
    catch { toast.error('Could not open call'); }
  };

  const book = async () => {
    if (!bookAt) return toast.error('Pick a date and time');
    setBooking(true);
    try {
      await api.post(`/api/voice/calls/${open.id}/book`, { scheduled_at: new Date(bookAt).toISOString(), notes: 'Booked from Voice AI' });
      toast.success('Walkthrough booked');
      setOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Booking failed'); }
    setBooking(false);
  };

  const lead = leads.find(l => l.id === leadId);
  return (
    <div className="space-y-6 animate-fade-in" data-testid="voice-closer-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-text-muted">Sales · Voice AI Closer</p>
          <h1 className="text-3xl font-heading font-black text-text-primary">Voice AI Closer</h1>
          <p className="text-sm text-text-muted mt-1 max-w-2xl">AI voice calls that qualify homeowners and book walkthroughs. {status && (status.live ? 'Live voice is connected — calls dial for real.' : 'Voice provider not connected yet — calls queue as rep call-tasks with an AI talk-track until Twilio keys are added.')}</p>
        </div>
        <div className={`text-xs font-bold px-3 py-1.5 rounded-full ${status?.live ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`} data-testid="voice-mode-badge">
          <i className={`fa-solid ${status?.live ? 'fa-phone-volume' : 'fa-list-check'} mr-1.5`} />{status?.live ? 'LIVE VOICE' : 'CALL-TASK MODE'}
        </div>
      </div>

      <div className="card p-5 space-y-4" data-testid="voice-place-card">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Lead to call</label>
            <select value={leadId} onChange={e => setLeadId(e.target.value)} className="input-field w-full" data-testid="voice-lead-select">
              <option value="">— pick a lead —</option>
              {leads.map(l => <option key={l.id} value={l.id}>{(l.owner_name || l.name || l.address || 'Lead')} · {l.phone || 'no phone'} · {l.city || ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Goal</label>
            <select value={goal} onChange={e => setGoal(e.target.value)} className="input-field" data-testid="voice-goal-select">
              <option value="qualify_book">Qualify + book walkthrough</option>
              <option value="book_only">Book the walkthrough</option>
              <option value="re_engage">Re-engage cold lead</option>
            </select>
          </div>
          <button onClick={place} disabled={placing || !leadId} className="px-6 py-3 rounded-xl text-sm font-black text-white bg-gradient-to-r from-emerald-500 to-green-600 disabled:opacity-50" data-testid="voice-place-btn">
            {placing ? <><i className="fa-solid fa-spinner fa-spin mr-2" />Dialing…</> : <><i className="fa-solid fa-phone mr-2" />Have AI call now</>}
          </button>
        </div>
        {lead && !lead.phone && <p className="text-xs text-amber-700"><i className="fa-solid fa-triangle-exclamation mr-1" />This lead has no phone. Run skip-trace from the lead page first.</p>}
      </div>

      {open && (
        <div className="card p-5 space-y-4 border-2 border-primary/40" data-testid="voice-call-detail">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3"><h3 className="font-heading font-bold text-text-primary">{open.lead_name || 'Call'}</h3>{badge(open.status)}</div>
            <button onClick={() => setOpen(null)} className="text-text-muted hover:text-text-primary" data-testid="voice-close-detail"><i className="fa-solid fa-xmark" /></button>
          </div>
          {open.dial && (
            <div className="rounded-xl p-4 bg-amber-50 border border-amber-200" data-testid="voice-talk-track">
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-1"><i className="fa-solid fa-phone mr-1" />Dial {open.dial.number} and say this</p>
              <p className="text-sm text-text-primary">“{open.dial.opener}”</p>
            </div>
          )}
          <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="voice-transcript">
            {(open.transcript || []).length === 0 && <p className="text-xs text-text-muted">Transcript will stream here as the call runs.</p>}
            {(open.transcript || []).map((t, i) => (
              <div key={i} className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${t.by === 'agent' ? 'bg-surface-alt ml-auto' : 'bg-primary/10'}`}>
                <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted mb-0.5">{t.by === 'agent' ? 'AI agent' : 'Homeowner'}{t.action ? ` · ${t.action}` : ''}</p>
                <p className="text-text-primary">{t.text}</p>
              </div>
            ))}
          </div>
          {open.status !== 'booked' && (
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-border">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">Book walkthrough</label>
                <input type="datetime-local" value={bookAt} onChange={e => setBookAt(e.target.value)} className="input-field" data-testid="voice-book-at" />
              </div>
              <button onClick={book} disabled={booking} className="px-5 py-2.5 rounded-xl text-sm font-bold bg-text-primary text-surface disabled:opacity-50" data-testid="voice-book-btn">
                {booking ? 'Booking…' : <><i className="fa-solid fa-calendar-check mr-2" />Book from this call</>}
              </button>
              {open.lead_id && <Link to={`/leads/${open.lead_id}`} className="text-xs font-bold text-primary hover:underline" data-testid="voice-open-lead">Open lead →</Link>}
            </div>
          )}
        </div>
      )}

      <div className="card overflow-hidden" data-testid="voice-call-log">
        <p className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-text-muted bg-surface-alt">Call log</p>
        {loading ? <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" /></div>
          : calls.length === 0 ? <p className="p-8 text-center text-sm text-text-muted">No AI calls yet — place the first one above.</p>
          : <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-text-muted"><tr><th className="text-left px-5 py-2">Lead</th><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Outcome</th><th className="text-left px-3 py-2">Mode</th><th className="px-5 py-2" /></tr></thead>
            <tbody>{calls.map(c => (
              <tr key={c.id} className="border-t border-border hover:bg-surface-alt/50">
                <td className="px-5 py-2.5 font-bold text-text-primary">{c.lead_name || '—'}<span className="block text-[11px] font-normal text-text-muted">{c.lead_phone || ''}</span></td>
                <td className="px-3 py-2.5 text-text-muted text-xs">{c.created_at ? new Date(c.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                <td className="px-3 py-2.5">{badge(c.status)}</td>
                <td className="px-3 py-2.5 text-xs text-text-muted">{c.mode === 'live-voice' ? 'Voice' : 'Task'}</td>
                <td className="px-5 py-2.5 text-right"><button onClick={() => view(c.id)} className="text-[11px] font-bold text-primary hover:underline" data-testid={`voice-view-${c.id}`}>Open</button></td>
              </tr>))}</tbody>
          </table>}
      </div>
    </div>
  );
}
